import {
  CircleCheck,
  Info,
  LoaderCircle,
  OctagonX,
  TriangleAlert,
} from "lucide-react"
import { Toaster as Sonner } from "sonner"

type ToasterProps = React.ComponentProps<typeof Sonner>

/**
 * App-level toast outlet (Sonner) with themed success/info/warning/error icons; mount once, fire toasts via `toast()`.
 *
 * @category Feedback
 */
const Toaster = ({ ...props }: ToasterProps) => {
  return (
    <Sonner
      theme="light"
      className="toaster group"
      // Wider toast than Sonner's 356px default; pairs with the larger padding
      // and text below for a more prominent bottom-right notification.
      style={{ "--width": "448px" } as React.CSSProperties}
      icons={{
        success: <CircleCheck className="h-5 w-5" />,
        info: <Info className="h-5 w-5" />,
        warning: <TriangleAlert className="h-5 w-5" />,
        error: <OctagonX className="h-5 w-5" />,
        loading: <LoaderCircle className="h-5 w-5 animate-spin" />,
      }}
      toastOptions={{
        classNames: {
          toast:
            "group toast group-[.toaster]:rounded-lg group-[.toaster]:border group-[.toaster]:shadow-modal group-[.toaster]:gap-3 group-[.toaster]:px-5 group-[.toaster]:py-4 group-[.toaster]:text-base",
          title: "group-[.toast]:text-base group-[.toast]:font-medium",
          default:
            "group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border",
          success:
            "group-[.toaster]:bg-success-soft group-[.toaster]:text-success-soft-foreground group-[.toaster]:border-success/30 [&_[data-icon]]:text-success",
          error:
            "group-[.toaster]:bg-destructive-soft group-[.toaster]:text-destructive-soft-foreground group-[.toaster]:border-destructive/30 [&_[data-icon]]:text-destructive",
          warning:
            "group-[.toaster]:bg-warning-soft group-[.toaster]:text-warning-soft-foreground group-[.toaster]:border-warning/30 [&_[data-icon]]:text-warning",
          info:
            "group-[.toaster]:bg-info-soft group-[.toaster]:text-info-soft-foreground group-[.toaster]:border-info/30 [&_[data-icon]]:text-info",
          description: "group-[.toast]:text-current/80",
          actionButton:
            "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton:
            "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
          // Sonner's built-in dismiss ("X") button is a 20x20 bordered
          // circle absolutely positioned at the toast's top-left corner.
          // Min-sizing the button itself is NOT an option: the enlarged
          // circle (border + background included) would extend ~24px into
          // the toast body, covering the leading icon and the start of the
          // title, and make a tap on the toast's first words dismiss it.
          // Instead, a transparent ::after extends the HIT AREA to 44x44
          // (20px box + 12px on each side) while the visible circle stays
          // 20px — the button is already position:absolute, so it is its
          // ::after's containing block.
          closeButton:
            "max-lg:after:absolute max-lg:after:-inset-3 max-lg:after:content-['']",
        },
      }}
      {...props}
    />
  )
}

export { Toaster }
